import React, { useEffect, useRef, useState } from "react";
import { Form, FormInstance, Input, InputRef, Tag, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import './Tags.scss';

interface PropsItem {
  form: FormInstance;
  className?: string;
  name: string;
  label?: React.ReactNode;
}

const Tags = ({ form, className, name, label }: PropsItem) => {
  const [tags, setTags] = useState<string[]>(form.getFieldValue(name) || []);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const inputRef = useRef<InputRef>(null);
  const editInputRef = useRef<InputRef>(null);
  let extraClass = `v-tags ${className || ''}`;

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [inputValue]);

  useEffect(() => {
    form.setFieldValue(name, tags);
  }, [name, tags]);

  function onClose(removedTag: string) {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
  }

  function onClickNew() {
    setInputVisible(true);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
  }

  function handleInputConfirm() {
    if (inputValue && tags.indexOf(inputValue) === -1) {
      setTags([...tags, inputValue]);
    }

    setInputVisible(false);
    setInputValue('');
  }

  function handleEditInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEditInputValue(e.target.value);
  }

  function handleEditInputConfirm() {
    const newTags = [...tags];
    newTags[editInputIndex] = editInputValue;

    setTags(newTags);
    setEditInputIndex(-1);
    setInputValue('');
  }

  const renderTag = (tag: string, index: number) => {
    if (editInputIndex === index) {
      return (
        <Input
          ref={editInputRef}
          key={tag}
          size="small"
          className="tag-input"
          value={editInputValue}
          onChange={handleEditInputChange}
          onBlur={handleEditInputConfirm}
          onPressEnter={handleEditInputConfirm}
        />
      );
    }

    const isLongTag = tag.length > 20;
    const tagElem = (
      <Tag
        className="edit-tag"
        key={tag}
        closable
        onClose={() => onClose(tag)}
      >
        <span onDoubleClick={e => {
          setEditInputIndex(index);
          setEditInputValue(tag);
          e.preventDefault();
        }}
        >
          {isLongTag ? `${tag.slice(0, 20)}...` : tag}
        </span>
      </Tag>
    );

    return isLongTag ? (
      <Tooltip title={tag} key={tag}>
        {tagElem}
      </Tooltip>
    ) : (
      tagElem
    );
  }

  return (
    <div className={extraClass}>
      <Form.Item name={name} label={label}>
        <Input type="hidden" />
      </Form.Item>

      {tags.map(renderTag)}

      {inputVisible && (
        <Input
          ref={inputRef}
          type="text"
          size="small"
          className="tag-input"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputConfirm}
          onPressEnter={handleInputConfirm}
        />
      )}

      {!inputVisible && (
        <Tag className="site-tag-plus" onClick={onClickNew}>
          <PlusOutlined /> New Tag
        </Tag>
      )}
    </div>
  )
}

export default Tags;
